/**
 * GuestCheckinForm
 *
 * Editorial re-skin of the per-guest check-in form, matching V2 of
 * shaka-deck-mockup-checkin.html. Four panels — Identity · Document ·
 * Preferences & safety · Consent — replace the prior flat sections.
 * BL-sync warning banner surfaces when the guest belongs to a
 * Bookinglayer-owned booking.
 *
 * Used inside `GuestCheckinDialog`; the dialog owns the page hero +
 * action bar framing, so this file scopes the panel layout + field
 * orchestration + the 4-button action row (Cancel · Save for later ·
 * Check in · Check in & pair wristband).
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocation } from '@/entities/location/hooks/useLocations';
import type { GuestFormField } from '@/entities/location/types';
import { requiredGuestFields } from '@/entities/location/utils/guestFormRequirements';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CheckCircle2, X, Radio } from 'lucide-react';

import { useTranslation } from '@/core';
import { Button } from '@/theme/components/ui/button';
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from '@/theme/components/ui/form';
import { Input } from '@/theme/components/ui/input';
import { Textarea } from '@/theme/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/theme/components/ui/select';
import { Checkbox } from '@/theme/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/theme/components/ui/alert-dialog';
import { notify } from '@/shared/services';
import { WristbandSyncDialog } from '@/shared/components/WristbandSyncDialog';
import { CountrySelect } from '@/shared/components/CountrySelect';
import { AllergenSelector } from '@/modules/inventory/components/AllergenSelector';
import { IdentityMatchPanel } from '@/shared/components/IdentityMatchPanel';
import { useLinkCheckin } from '@/entities/person/hooks/usePersons';
import { splitName, type CheckinMatchCandidate, type Person } from '@/entities/person/types';
import type { Guest } from '../types';
import { checkInGuest as checkInGuestService } from '../services/guestService';

// ── Schema ──────────────────────────────────────────────────────────

const guestCheckinSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  secondLastName: z.string().max(100).optional(),
  // Important but never blocking: front desk must be able to complete a
  // check-in without an email address.
  email: z.string().email().max(255).optional().or(z.literal('')),
  phone: z.string().optional(),
  birthDate: z.string().optional(),
  nationalityIso: z.string().max(2).optional().or(z.literal('')),
  // Document is no longer blocking at check-in: front desk can complete a
  // stay without an ID/passport and capture it later. The backend accepts a
  // check-in with no document, but rejects a half-filled pair, so we keep
  // type + number coherent (see the paired refines below).
  documentType: z.string().optional().or(z.literal('')),
  documentNumber: z.string().max(50).optional().or(z.literal('')),
  expirationDate: z.string().optional(),
  noExpiration: z.boolean().optional(),
  allergies: z.array(z.string()).default([]),
  specialRequests: z.string().optional(),
  internalNotes: z.string().optional(),
  legalAgreement: z.boolean().refine((v) => v === true),
  marketingOptIn: z.boolean().optional(),
  notes: z.string().optional(),
}).refine(
  // If a document type is chosen, the number is required (mirrors backend).
  (data) => !data.documentType || !!data.documentNumber,
  { path: ['documentNumber'] },
).refine(
  // If a document number is entered, the type is required (mirrors backend).
  (data) => !data.documentNumber || !!data.documentType,
  { path: ['documentType'] },
);

export type GuestCheckinFormData = z.infer<typeof guestCheckinSchema>;

/**
 * The base schema plus whatever THIS LOCATION requires (#35).
 *
 * Nothing above is required beyond the name: that is the pre-setting
 * behaviour and stays the default. A site that marks a field required gets a
 * refine per field here, so the individual form and the batch check-in reach
 * the same verdict about the same guest — they used to disagree about the
 * identity document, which is where #35 came from.
 *
 * GDPR (Art. 5.1.c): these refines only ever fire for fields a site has
 * deliberately turned on.
 */
function schemaFor(required: GuestFormField[]) {
  const needs = (field: GuestFormField) => required.includes(field);
  let schema: z.ZodTypeAny = guestCheckinSchema;

  if (needs('document')) {
    schema = schema.refine(
      (d: GuestCheckinFormData) => !!d.documentType && !!d.documentNumber,
      { path: ['documentNumber'] },
    );
  }
  if (needs('document_expiration')) {
    schema = schema.refine(
      (d: GuestCheckinFormData) => !!d.expirationDate || d.noExpiration === true,
      { path: ['expirationDate'] },
    );
  }
  if (needs('birth_date')) {
    schema = schema.refine((d: GuestCheckinFormData) => !!d.birthDate, { path: ['birthDate'] });
  }
  if (needs('nationality')) {
    schema = schema.refine(
      (d: GuestCheckinFormData) => !!d.nationalityIso, { path: ['nationalityIso'] },
    );
  }
  if (needs('email')) {
    schema = schema.refine((d: GuestCheckinFormData) => !!d.email, { path: ['email'] });
  }
  if (needs('phone')) {
    schema = schema.refine((d: GuestCheckinFormData) => !!d.phone, { path: ['phone'] });
  }
  return schema;
}

/** Age in full years from a YYYY-MM-DD string, or null when not computable. */
function computeAge(birthDate?: string): number | null {
  if (!birthDate) return null;
  const parsed = new Date(birthDate);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }
  return age >= 0 && age < 130 ? age : null;
}

export interface GuestCheckinFormProps {
  guest: Guest;
  /**
   * When true, renders the BL-managed warning banner at the top. The
   * dialog caller already knows whether the parent booking is BL-owned.
   */
  blManaged?: boolean;
  /** When true, guest-lifecycle sync-out is OFF — documented inline. */
  stayLifecycleSyncOff?: boolean;
  onComplete?: (updatedGuest: Guest) => void;
  onCancel?: () => void;
  /** Optional "save for later" action. Defaults to onCancel behaviour. */
  onSaveForLater?: (data: GuestCheckinFormData) => void;
  isLoading?: boolean;
  onSubmit?: (data: GuestCheckinFormData) => Promise<void>;
}

// ── Panel primitive (shared with the 4 sections) ─────────────────────

function Panel({
  title,
  meta,
  children,
}: {
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[var(--radius)] border border-border bg-card px-5 py-[18px] shadow-sm">
      <header className="mb-3.5 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-serif text-[17px] font-semibold tracking-[-0.01em] text-sand-900">
          {title}
        </h3>
        {meta ? (
          <span className="font-mono text-[11.5px] text-muted-foreground">{meta}</span>
        ) : null}
      </header>
      {children}
    </section>
  );
}

// ── Component ────────────────────────────────────────────────────────

export const GuestCheckinForm: React.FC<GuestCheckinFormProps> = ({
  guest,
  blManaged = false,
  stayLifecycleSyncOff = false,
  onComplete,
  onCancel,
  onSaveForLater,
  isLoading: externalLoading,
  onSubmit: customSubmit,
}) => {
  const { t } = useTranslation();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showWristbandSync, setShowWristbandSync] = useState(false);

  const { activeLocationId } = useActiveContext();
  const { data: location } = useLocation(activeLocationId ?? undefined);
  const requiredFields = requiredGuestFields(location);
  // Rebuilt only when the site's requirements change, so typing does not
  // recreate the resolver on every keystroke.
  const resolverSchema = useMemo(
    () => schemaFor(requiredFields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requiredFields.join('|')],
  );

  const form = useForm<GuestCheckinFormData>({
    resolver: zodResolver(resolverSchema),
    defaultValues: {
      firstName: guest.firstName || '',
      lastName: guest.lastName || '',
      secondLastName: '',
      email: guest.email || '',
      phone: guest.phone || '',
      birthDate: guest.person?.birthDate || '',
      nationalityIso: guest.person?.nationalityIso || '',
      documentType: guest.person?.documentType || '',
      documentNumber: guest.person?.documentNumber || '',
      expirationDate: '',
      noExpiration: false,
      allergies: guest.person?.allergies || [],
      specialRequests: '',
      internalNotes: '',
      legalAgreement: false,
      marketingOptIn: false,
      notes: guest.person?.notes || '',
    },
  });

  const isLoading = externalLoading || form.formState.isSubmitting;
  const noExpiration = form.watch('noExpiration');
  const watchedBirthDate = form.watch('birthDate');
  const watchedFirstName = form.watch('firstName');
  const watchedLastName = form.watch('lastName');
  const watchedEmail = form.watch('email');
  const watchedDocumentNumber = form.watch('documentNumber');
  const guestAge = computeAge(watchedBirthDate);
  const queryClient = useQueryClient();

  const [pendingLink, setPendingLink] = useState<CheckinMatchCandidate | null>(null);
  // Once an identity is chosen, the panel collapses to a confirmation and stops
  // matching (see IdentityMatchPanel). Cleared via "Change".
  const [linkedPerson, setLinkedPerson] = useState<{ id: string; name: string } | null>(null);
  const linkCheckin = useLinkCheckin();

  const candidateName = (c: CheckinMatchCandidate) =>
    c.person.name || `${c.person.firstName ?? ''} ${c.person.lastName ?? ''}`.trim() || t('common.unknown', 'Unknown');

  const prefillFromPerson = (p: Person) => {
    const first = p.firstName || splitName(p.name).firstName;
    const last = p.lastName || splitName(p.name).lastName;
    if (first) form.setValue('firstName', first, { shouldValidate: true });
    if (last) form.setValue('lastName', last, { shouldValidate: true });
    if (p.email) form.setValue('email', p.email);
    if (p.birthDate) form.setValue('birthDate', p.birthDate);
    if (p.nationalityIso) form.setValue('nationalityIso', p.nationalityIso);
    if (p.documentType) form.setValue('documentType', p.documentType);
    if (p.documentNumber) form.setValue('documentNumber', p.documentNumber);
  };

  // "This is them" — consolidate the current booking's fresh person into the
  // matched existing one (destructive, so confirm first). When there's no
  // distinct source person to consolidate, just adopt the details.
  const handleUseProfile = (candidate: CheckinMatchCandidate) => {
    const sourcePersonId = guest.person?.id;
    if (!sourcePersonId || sourcePersonId === candidate.person.id) {
      prefillFromPerson(candidate.person);
      setLinkedPerson({ id: candidate.person.id, name: candidateName(candidate) });
      notify.success(t('guests.checkin.match.linked', "Using {{name}}'s profile", { name: candidateName(candidate) }));
      return;
    }
    setPendingLink(candidate);
  };

  const confirmLink = () => {
    const candidate = pendingLink;
    const sourcePersonId = guest.person?.id;
    if (!candidate || !sourcePersonId) { setPendingLink(null); return; }
    linkCheckin.mutate(
      { targetId: candidate.person.id, sourcePersonId },
      {
        onSuccess: () => {
          prefillFromPerson(candidate.person);
          setLinkedPerson({ id: candidate.person.id, name: candidateName(candidate) });
          notify.success(
            t('guests.checkin.match.linkedConsolidated', 'Linked to {{name}} — records consolidated', { name: candidateName(candidate) }),
          );
          setPendingLink(null);
        },
        onError: (err) => {
          notify.error(err instanceof Error ? err.message : t('guests.checkin.match.linkError', 'Could not link the guest'));
          setPendingLink(null);
        },
      },
    );
  };

  const handleSubmit = async (data: GuestCheckinFormData, shouldSyncWristband = false) => {
    try {
      if (customSubmit) {
        await customSubmit(data);
      } else {
        const updated = await checkInGuestService(guest.id, {
          firstName: data.firstName,
          lastName: data.lastName,
          secondLastName: data.secondLastName || '',
          email: data.email || '',
          phone: data.phone,
          birthDate: data.birthDate || '',
          nationalityIso: data.nationalityIso || '',
          documentType: data.documentType,
          documentNumber: data.documentNumber,
          documentExpiration: data.noExpiration ? null : (data.expirationDate || null),
          allergies: data.allergies,
          notes: [data.notes, data.internalNotes].filter(Boolean).join('\n'),
          specialRequests: data.specialRequests || '',
          marketingOptIn: data.marketingOptIn ?? false,
          dataConsentGiven: data.legalAgreement,
        });
        // Default path used the service directly without invalidating React
        // Query caches, so guest/booking lists could show stale state until a
        // manual refetch. Invalidate the same keys the mutation hook does.
        queryClient.invalidateQueries({ queryKey: ['guests'] });
        queryClient.invalidateQueries({ queryKey: ['bookingDetail'] });
        queryClient.invalidateQueries({ queryKey: ['bookingGuests'] });
        notify.success(t('guest.checkedIn', { name: `${data.firstName} ${data.lastName}` }));
        if (shouldSyncWristband) setShowWristbandSync(true);
        else onComplete?.(updated);
      }
    } catch (error) {
      notify.error(error instanceof Error ? error.message : t('guest.checkinError'));
    }
  };

  const handleCancel = () => {
    if (form.formState.isDirty) setShowCancelDialog(true);
    else { form.reset(); onCancel?.(); }
  };

  const confirmCancel = () => { form.reset(); setShowCancelDialog(false); onCancel?.(); };

  const handleFinishWristbandSync = () => {
    setShowWristbandSync(false);
    notify.success(t('guest.wristbandComplete'));
    onComplete?.(guest);
  };

  return (
    <>
      <Form {...form}>
        <form className="flex min-h-0 flex-1 flex-col">
          {/* Scrolling body — fields scroll; the action bar below stays pinned. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* BL-managed warning banner */}
          {blManaged ? (
            <div className="flex gap-3 rounded-md border border-ocean-200 bg-ocean-50 px-4 py-3 text-[12.5px] leading-[1.55] text-ocean-900">
              <span aria-hidden="true" className="text-[16px] leading-none text-ocean-700">◈</span>
              <div>
                <b>{t('guest.checkin.bl.title', 'This guest is managed by Bookinglayer.')}</b>{' '}
                {t(
                  'guest.checkin.bl.body',
                  'Guest data & Documents sync is ON — edits here push back to BL on save.',
                )}
                {stayLifecycleSyncOff ? (
                  <>
                    {' '}
                    {t(
                      'guest.checkin.bl.lifecycleOff',
                      "Stay lifecycle sync is OFF — check-in won't be mirrored to BL until enabled in SyncConfig.",
                    )}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Guest identity match — recover a returning guest before entering data */}
          <IdentityMatchPanel
            context="guest_checkin"
            firstName={watchedFirstName}
            lastName={watchedLastName}
            email={watchedEmail}
            birthDate={watchedBirthDate}
            documentNumber={watchedDocumentNumber}
            currentPersonId={guest.person?.id}
            onUseProfile={handleUseProfile}
            linkedPersonName={linkedPerson?.name ?? null}
            onChangeLink={() => setLinkedPerson(null)}
          />

          {/* Identity */}
          <Panel
            title={t('guest.checkin.panels.identity.title', 'Identity')}
            meta={t('guest.checkin.panels.identity.meta', '2 required')}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField control={form.control} name="firstName" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.firstName')} *</FormLabel>
                  <FormControl><Input {...field} placeholder={t('guest.checkinForm.firstNamePlaceholder', 'John')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="lastName" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.lastName')} *</FormLabel>
                  <FormControl><Input {...field} placeholder={t('guest.checkinForm.lastNamePlaceholder', 'Doe')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="secondLastName" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.secondLastName')}</FormLabel>
                  <FormControl><Input {...field} placeholder={t('guest.checkinForm.secondLastNamePlaceholder', 'Smith')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField control={form.control} name="birthDate" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.birthDate', 'Birth date')}</FormLabel>
                  <FormControl><Input {...field} type="date" max={new Date().toISOString().slice(0, 10)} /></FormControl>
                  {guestAge !== null && (
                    <p
                      className={guestAge >= 18
                        ? 'text-xs text-muted-foreground'
                        : 'text-xs font-medium text-amber-600'}
                    >
                      {guestAge >= 18
                        ? t('guest.checkin.age.adult', 'Adult · {{age}} years old', { age: guestAge })
                        : t('guest.checkin.age.minor', 'Minor · {{age}} years old', { age: guestAge })}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="nationalityIso" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.nationality')}</FormLabel>
                  <FormControl>
                    <CountrySelect value={field.value ?? ''} onChange={(code) => field.onChange(code)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="phone" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.phone')}</FormLabel>
                  <FormControl><Input {...field} type="tel" placeholder={t('guest.checkinForm.phonePlaceholder', '+1234567890')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <div className="mt-3">
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.email')}</FormLabel>
                  <FormControl><Input {...field} type="email" placeholder={t('guest.checkinForm.emailPlaceholder', 'john.doe@example.com')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
          </Panel>

          {/* Document */}
          <Panel
            title={t('guest.checkin.panels.document.title', 'Document')}
            meta={t('guest.checkin.panels.document.meta', 'encrypted · Art. 32 GDPR')}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField control={form.control} name="documentType" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.documentType')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder={t('guest.selectType')} /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="passport">{t('guest.passport')}</SelectItem>
                      <SelectItem value="id_card">{t('guest.nationalId')}</SelectItem>
                      <SelectItem value="driving_license">{t('guest.driversLicense')}</SelectItem>
                      <SelectItem value="other">{t('guest.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="documentNumber" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.documentNumber')}</FormLabel>
                  <FormControl><Input {...field} placeholder={t('guest.checkinForm.documentNumberPlaceholder', 'A12345678')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="expirationDate" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.expirationDate')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" disabled={noExpiration} className={noExpiration ? 'opacity-50' : ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <div className="mt-3">
              <FormField control={form.control} name="noExpiration" render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="font-normal">{t('guest.noExpirationDate')}</FormLabel>
                </FormItem>
              )} />
            </div>
          </Panel>

          {/* Preferences & safety */}
          <Panel
            title={t('guest.checkin.panels.preferences.title', 'Preferences & safety')}
            meta={t('guest.checkin.panels.preferences.meta', 'mirrors Person record · kitchen sees allergies')}
          >
            <FormField control={form.control} name="allergies" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('person.fields.allergies', 'Allergies')}</FormLabel>
                <FormControl>
                  <AllergenSelector value={field.value ?? []} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="mt-3">
              <FormField control={form.control} name="specialRequests" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.specialRequests')}</FormLabel>
                  <FormControl><Textarea {...field} placeholder={t('guest.specialRequestsPlaceholder')} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <div className="mt-3">
              <FormField control={form.control} name="internalNotes" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guest.checkin.internalNotes.label', 'Internal notes (not shared with guest)')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t('guest.checkin.internalNotes.placeholder', 'Context for kitchen, housekeeping, or next shift…')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
          </Panel>

          {/* Consent */}
          <Panel
            title={t('guest.checkin.panels.consent.title', 'Consent')}
            meta={t('guest.checkin.panels.consent.meta', 'Art. 6 GDPR · explicit · revocable')}
          >
            <FormField control={form.control} name="legalAgreement" render={({ field }) => (
              <FormItem className="flex items-start space-x-3 space-y-0 rounded-md border border-border bg-sand-50 p-3">
                <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="font-semibold">{t('guest.termsAndConditions')} *</FormLabel>
                  <FormDescription>{t('guest.termsDescription')}</FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )} />
            <div className="mt-2">
              <FormField control={form.control} name="marketingOptIn" render={({ field }) => (
                <FormItem className="flex items-start space-x-3 space-y-0 rounded-md border border-dashed border-border px-3 py-2">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">
                      {t('guest.checkin.marketing.label', 'Opt-in · Marketing emails about future stays, events, and offers.')}
                    </FormLabel>
                    <FormDescription className="italic">
                      {t('guest.checkin.marketing.description', "Can be revoked at any time from the guest's profile.")}
                    </FormDescription>
                  </div>
                </FormItem>
              )} />
            </div>
          </Panel>

          </div>

          {/* Pinned footer — a real footer of the modal (flex-none), not a
              sticky element inside the scroll area, so nothing peeks past it. */}
          <div className="flex flex-none flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-5 py-3 shadow-[0_-8px_16px_-12px_hsl(var(--foreground)/0.15)]">
            <Button type="button" variant="ghost" onClick={handleCancel} disabled={isLoading}>
              <X className="mr-2 h-4 w-4" />
              {t('common.cancel')}
            </Button>
            <div className="flex flex-wrap gap-2">
              {onSaveForLater ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={form.handleSubmit((data) => onSaveForLater(data))}
                  disabled={isLoading}
                >
                  {t('guest.checkin.saveForLater', 'Save for later')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={form.handleSubmit((data) => handleSubmit(data, false))}
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                {t('guest.checkin.actions.checkin', 'Check in')}
              </Button>
              <Button
                type="button"
                className="bg-coral-500 text-white hover:bg-coral-600"
                onClick={form.handleSubmit((data) => handleSubmit(data, true))}
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Radio className="mr-2 h-4 w-4" />}
                {t('guest.checkin.actions.checkinAndPair', 'Check in & pair wristband →')}
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {/* Wristband Sync Dialog */}
      <WristbandSyncDialog
        open={showWristbandSync}
        onOpenChange={(open) => { if (!open) handleFinishWristbandSync(); }}
        personId={guest.person?.id ?? guest.id}
        personName={`${guest.firstName} ${guest.lastName}`}
        locationId={guest.locationId}
        skippable
        onSkip={handleFinishWristbandSync}
        onSuccess={handleFinishWristbandSync}
      />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[20px] font-semibold tracking-[-0.01em]">
              {t('guest.unsavedChanges')}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('guest.unsavedChangesDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('guest.continueEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel}>{t('guest.discardChanges')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingLink} onOpenChange={(open) => { if (!open) setPendingLink(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[20px] font-semibold tracking-[-0.01em]">
              {t('guests.checkin.match.confirmTitle', 'Link to existing guest?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'guests.checkin.match.confirmBody',
                "This consolidates the current booking into {{name}}'s existing profile and moves the wristband. It can't be undone.",
                { name: pendingLink?.person.name ?? '' },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.actions.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLink}
              disabled={linkCheckin.isPending}
              className="bg-coral-500 text-white hover:bg-coral-600"
            >
              {linkCheckin.isPending
                ? t('guests.checkin.match.linking', 'Linking…')
                : t('guests.checkin.match.confirmAction', 'Link & consolidate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
